import { Box, Button, FormControl, Input, InputLabel, Link, MenuItem, Select, Snackbar, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { logoTransparent } from '../../../assets';
import ContactMap from '../ContactMap/ContactMap';
import { FlipCard, ScrollFadeInSlideUp, SectionTitle } from '../../../components';
import ContactStatement from '../ContactStatement/ContactStatement';
import ContactPrivacyPolicy from '../ContactPrivacyPolicy/ContactPrivacyPolicy';
import emailjs from 'emailjs-com'; // Import EmailJS

const ContactForm = ({ sx }) => {
    const regexPattern = {
        name: /^[a-zA-Z\s]*$/,
        email: /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,7}$/,
        message: /^[\s\S]+$/,
        number: /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
    };

    const initialFormFields = [
        { name: 'Inquiry', id: 'inquiry', placeholder: 'Select Inquiry', type: 'select', selected: false, value: '', 
            items: [
                "Commercial Construction",
                "Design-Build Services",
                "Industrial Construction",
                "Institutional Projects",
                "Interior Design",
                "Pre-Construction Consultation",
                "Project Management",
                "Renovation & Remodeling",
                "Residential Construction",
                "Sustainable Building Solutions",
                "General Inquiry"
            ]
        },
        { name: 'Region', id: 'region', placeholder: 'Select Your region', type: 'select', selected: false, value: '', 
            items: [
                "Lower Mainland / Vancouver Area", 
                "Cariboo",
                "Central Coast / North Coast",
                "Fraser Valley", 
                "Kootenays",
                "Northern BC",
                "Okanagan",
                "Thompson-Nicola",
                "Vancouver Island",
                "Other (Outside BC)"
            ]
        },
        { name: 'First Name', id: 'firstName', placeholder: 'Your First Name', validation: regexPattern.name, type: 'input', value: '', selected: false },
        { name: 'Last Name', id: 'lastName', placeholder: 'Your Last Name', validation: regexPattern.name, type: 'input', value: '', selected: false },
        { name: 'Email', id: 'email', placeholder: 'Your Email Address', validation: regexPattern.email, type: 'input', value: '', selected: false },
        { name: 'Phone Number', id: 'phoneNumber', placeholder: 'Your Phone Number', validation: regexPattern.number, type: 'input', value: '', selected: false },
        { name: 'Message', id: 'message', placeholder: 'Description', validation: regexPattern.message, type: 'text', value: '', selected: false },
        { name: 'Button', id: 'submitButton', type: 'button' },
        { name: 'Terms', id: 'terms', type: 'terms' },
    ];

    const [formFields, setFormFields] = useState(initialFormFields);
    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false); // Snackbar state for success message

    const formContainerRef = useRef(null); // Reference to the form container
    const [formHeight, setFormHeight] = useState(0); // State to store the height of the form container

    // Honeypot state
    const [honeypotValue, setHoneypotValue] = useState(''); // Honeypot field value

    useEffect(() => {
        // Dynamically calculate the height of the form container
        if (formContainerRef.current) {
            setFormHeight(formContainerRef.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        validateForm();
    }, [formFields, errors]);

    const validateForm = () => {
        const isValid = formFields.every((field) => {
            if (field.type === 'input' || field.type === 'text') {
                return field.value && (!field.validation || field.validation.test(field.value));
            } else if (field.type === 'select') {
                return field.value && field.items.includes(field.value); // Ensure value is in the list
            }
            return true; // Skip validation for other field types
        });

        setIsFormValid(isValid);
    };

    const handleFocus = (id) => {
        setFormFields((prevFields) =>
            prevFields.map((field) =>
                field.id === id ? { ...field, selected: true } : field
            )
        );
    };

    const handleBlur = (id, value) => {
        setFormFields((prevFields) =>
            prevFields.map((field) =>
                field.id === id ? { ...field, selected: false } : field
            )
        );

        // Validate the field
        const field = formFields.find((field) => field.id === id);
        if (field) {
            const isValid = field.validation ? field.validation.test(value) : true;
            setErrors((prevErrors) => ({
                ...prevErrors,
                [id]: isValid ? '' : `${field.name} is invalid or required`,
            }));
        }

        // Recalculate form validity
        validateForm();
    };

    const handleSelectChange = (id, value) => {
        setFormFields((prevFields) =>
            prevFields.map((field) =>
                field.id === id ? { ...field, value } : field
            )
        );

        // Validate the field
        const field = formFields.find((field) => field.id === id);
        if (field) {
            const isValid = field.items ? field.items.includes(value) : !!value;
            setErrors((prevErrors) => ({
                ...prevErrors,
                [id]: isValid ? '' : `${field.name} is invalid or required`,
            }));
        }

        // Recalculate form validity
        validateForm();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Form submitted!");
    
        // Check honeypot field
        if (honeypotValue) {
            console.log("Bot detected! Form submission rejected.");
            return;
        }
    
        // Prepare the data to send
        const sanitizeInput = (input) => input.replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const templateParams = {
            inquiry: sanitizeInput(formFields.find((field) => field.id === 'inquiry')?.value || 'N/A'),
            region: sanitizeInput(formFields.find((field) => field.id === 'region')?.value || 'N/A'),
            firstName: sanitizeInput(formFields.find((field) => field.id === 'firstName')?.value || 'N/A'),
            lastName: sanitizeInput(formFields.find((field) => field.id === 'lastName')?.value || 'N/A'),
            email: sanitizeInput(formFields.find((field) => field.id === 'email')?.value || 'N/A'),
            phoneNumber: sanitizeInput(formFields.find((field) => field.id === 'phoneNumber')?.value || 'N/A'),
            message: sanitizeInput(formFields.find((field) => field.id === 'message')?.value || 'N/A'),
        };
    
        // Send the email using EmailJS
        emailjs
            .send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                templateParams,
                process.env.REACT_APP_EMAILJS_USER_ID
            )
            .then(
                (response) => {
                    console.log('SUCCESS!', response.status, response.text);
                    setFormFields(initialFormFields); // Reset all fields
                    setIsSnackbarOpen(true); // Show success message
                },
                (error) => {
                    console.error('FAILED...', error);
                    alert('Failed to send the email. Please try again later.');
                }
            );
    };

    const handleSnackbarClose = () => {
        setIsSnackbarOpen(false);
    };

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
    };

    return (
        <Box
            sx={{
                ...sx,
                backgroundColor: 'rgba(245, 245, 239, 1)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginX: '10rem',
                    paddingY: '5rem',
                    '@media screen and (max-width:762px)': {
                        marginX: '0',
                        paddingY: '2rem',
                    },
                }}
            >
                <FlipCard>
                    <Box
                        sx={{
                            width: '5rem',
                        }}
                    >
                        <Box
                            component="img"
                            src={logoTransparent}
                            sx={{
                                width: '5rem',
                                display: 'flex',
                            }}
                        />
                    </Box>
                </FlipCard>
                <Box>
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: '2rem',
                            fontFamily: "var(--title-font)",
                            textTransform: 'Uppercase',
                            fontWeight: '1000',
                            alignContent: 'center',
                            alignItems: 'center',
                            letterSpacing: '0.5rem',
                            textAlign: 'center',
                            lineHeight: '4rem',
                            fontStretch: 'expanded',
                            marginX: '1rem',
                            color: '#000',
                        }}
                    >
                        Pangu
                    </Typography>
                </Box>
            </Box>

            <ContactStatement />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    paddingBottom: '5rem',
                    '@media screen and (max-width:762px)': {
                        flexDirection: 'column',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: '50%',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        alignItems: 'center',
                        '@media screen and (max-width:762px)': {
                            width: '100%',
                        },
                    }}
                >
                    <Box
                        ref={formContainerRef}
                        sx={{
                            paddingY: '2rem',
                            width: '100%',
                        }}
                    >
                        <SectionTitle
                            firstTitleWord="How Can"
                            secondTitleWord="We Help?"
                            textStyle={{
                                fontWeight: '1000',
                                paddingY: '1rem',
                            }}
                            sx={{
                                textAlign: 'left',
                                paddingY: '0',
                                width: '100%',
                                '@media screen and (max-width:762px)': {
                                    paddingY: '0',
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                textAlign: 'center',
                                fontFamily: 'var(--text-font)',
                                textTransform: 'uppercase',
                                fontSize: '1.2rem',
                            }}
                        >
                            Tell us more!
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '70%',
                            boxSizing: 'border-box',
                            padding: '2rem',
                            boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.3)',
                            backgroundColor: '#fefefe',
                            alignItems: 'center',
                            '@media screen and (max-width: 762px)': {
                                width: '90%',
                            },
                        }}
                    >
                        <ScrollFadeInSlideUp>
                            <form onSubmit={handleSubmit}>
                                {/* Honeypot Field */}
                                <input
                                    type="text"
                                    name="honeypot"
                                    style={{ display: 'none' }} // Hide the honeypot field
                                    value={honeypotValue}
                                    onChange={(e) => setHoneypotValue(e.target.value)}
                                />
                                <FormControl
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '1rem',
                                        flexDirection: 'row',
                                    }}
                                >
                                    {formFields.map((item, index) => {
                                        if (item.type === 'input') {
                                            return (
                                                <FormControl
                                                    sx={{
                                                        flex: '1 1 calc(50% - 1rem)',
                                                    }}
                                                    key={item.id}
                                                >
                                                    <InputLabel
                                                        htmlFor={item.id}
                                                        shrink={!!item.value}
                                                        sx={{
                                                            transition: 'ease-in 0.3s',
                                                        }}
                                                    >
                                                        {item.name}
                                                    </InputLabel>
                                                    <Input
                                                        id={item.id}
                                                        name={item.name}
                                                        placeholder={item.placeholder}
                                                        value={item.value}
                                                        onFocus={() => handleFocus(item.id)}
                                                        onBlur={(e) => handleBlur(item.id, e.target.value)}
                                                        onChange={(e) => handleSelectChange(item.id, e.target.value)}
                                                        fullWidth
                                                    />
                                                    {errors[item.id] && (
                                                        <Typography color="error" sx={{ fontSize: '0.8rem' }}>
                                                            {errors[item.id]}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            );
                                        } else if (item.type === 'text') {
                                            return (
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        flex: '1 1 100%',
                                                    }}
                                                    key={item.id}
                                                >
                                                    <TextField
                                                        id={item.id}
                                                        name={item.name}
                                                        label={item.name}
                                                        multiline
                                                        rows={4}
                                                        value={item.value}
                                                        onBlur={(e) => handleBlur(item.id, e.target.value)}
                                                        onChange={(e) => handleSelectChange(item.id, e.target.value)}
                                                        fullWidth
                                                    />
                                                    {errors[item.id] && (
                                                        <Typography color="error" sx={{ fontSize: '0.8rem' }}>
                                                            {errors[item.id]}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            );
                                        } else if (item.type === 'select') {
                                            return (
                                                <FormControl
                                                    sx={{
                                                        flex: '1 1 calc(50% - 1rem)',
                                                    }}
                                                    key={item.id}
                                                >
                                                    <InputLabel>{item.name}</InputLabel>
                                                    <Select
                                                        value={item.value}
                                                        onChange={(event) =>
                                                            handleSelectChange(item.id, event.target.value)
                                                        }
                                                        label={item.placeholder}
                                                    >
                                                        {item.items.map((option, index) => (
                                                            <MenuItem key={index} value={option}>
                                                                {option}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors[item.id] && (
                                                        <Typography color="error" sx={{ fontSize: '0.8rem' }}>
                                                            {errors[item.id]}
                                                        </Typography>
                                                    )}
                                                </FormControl>
                                            );
                                        } else if (item.type === 'button') {
                                            return (
                                                <Button
                                                    type="submit"
                                                    variant="outlined"
                                                    disabled={!isFormValid}
                                                    aria-disabled={!isFormValid}
                                                    title={!isFormValid ? "Please fill out all required fields correctly." : ""}
                                                    sx={{
                                                        flex: '1 1 calc(50% - 1rem)',
                                                        borderColor: '#000',
                                                        color: '#000',
                                                    }}
                                                >
                                                    Submit
                                                </Button>
                                            );
                                        } else if (item.type) {
                                            return (
                                                <Typography
                                                    sx={{
                                                        flex: '1 1 calc(50% - 1rem)',
                                                        fontFamily: 'var(--text-font)',
                                                        fontSize: '0.7rem',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    By clicking{' '}
                                                    <span style={{ fontWeight: 'bold' }}>Submit</span> you have read and
                                                    understood our{' '}
                                                    <Link
                                                        onClick={handleDialogOpen}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                        }}
                                                    >
                                                        Privacy Policy
                                                    </Link>
                                                    {isDialogOpen && (
                                                        <ContactPrivacyPolicy
                                                            open={isDialogOpen}
                                                            onClose={handleDialogClose}
                                                        />
                                                    )}
                                                </Typography>
                                            );
                                        } else {
                                            return errors;
                                        }
                                    })}
                                </FormControl>
                            </form>
                        </ScrollFadeInSlideUp>
                    </Box>
                </Box>

                <Box
                    sx={{
                        paddingX: '5rem',
                        boxSizing: 'border-box',
                        width: '50%',
                        marginTop: `${formHeight}px`,
                        '@media screen and (max-width: 762px)': {
                            width: '100%',
                            marginTop: '2rem',
                            paddingX: '2rem',
                            height: '30vh',
                        },
                    }}
                >
                    <ScrollFadeInSlideUp sx={{ height: '100%' }}>
                        <ContactMap />
                    </ScrollFadeInSlideUp>
                </Box>
            </Box>

            {/* Snackbar for Success Message */}
            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message="Form submitted successfully!"
                aria-live="polite"
            />
        </Box>
    );
};

export default ContactForm;